/**
 * Code-default layouts for the multi-building campus.
 * Outdoor hub + 6 buildings (Reception, Cafe, Delivery, Post Delivery,
 * People+Marketing, Customer Success).
 */
import {
  type DoorWall,
  MAP_COLS,
  MAP_ROWS,
  makePodFromBounds,
  type PersonalOfficeSlotLayout,
} from "@/lib/office/pod-bounds";

/** Local document types — do not import personal-office-layout (avoids circular init). */
type MapFurniture = {
  id: string;
  kind: string;
  gx: number;
  gy: number;
  w: number;
  d: number;
  dir: DoorWall;
  seat?: boolean;
  block?: boolean;
  c?: string;
  displayName?: string;
  doorWall?: DoorWall;
  style?: string;
};
type MapTransition = {
  id: string;
  gx: number;
  gy: number;
  toMapId: string;
  toSpawn: { gx: number; gy: number };
  label?: string;
};
type CommonZoneLayout = {
  id: string;
  name: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  rug: string;
  priv?: boolean;
  roomKind?: "meeting" | "huddle";
};
export type CampusMapLayoutDocument = {
  id: string;
  cols: number;
  rows: number;
  offices: PersonalOfficeSlotLayout[];
  commonZones: CommonZoneLayout[];
  furniture: MapFurniture[];
  spawnPoints: Array<{ gx: number; gy: number }>;
  transitions: MapTransition[];
};

const POD = 4; // inclusive width/height = POD+1 = 5
const STRIDE = 4; // shared partition wall

function furn(
  id: string,
  kind: string,
  gx: number,
  gy: number,
  opts?: Partial<Pick<MapFurniture, "w" | "d" | "dir" | "seat" | "block">>,
): MapFurniture {
  return {
    id,
    kind,
    gx,
    gy,
    w: opts?.w ?? 1,
    d: opts?.d ?? 1,
    dir: opts?.dir ?? "S",
    seat: opts?.seat,
    block: opts?.block,
  };
}

function cloneOffices(offices: PersonalOfficeSlotLayout[]): PersonalOfficeSlotLayout[] {
  return offices.map((s) => ({
    ...s,
    door: { ...s.door },
    interiorSeat: { ...s.interiorSeat },
    desk: { ...s.desk },
    decorSlots: s.decorSlots.map((d) => ({ ...d })),
    wallArt: { ...s.wallArt },
    wallArtSlots: (s.wallArtSlots ?? [s.wallArt]).map((w) => ({ ...w })),
  }));
}

/** Horizontal row of shared-wall pods, doors facing `doorWall`. */
function buildPodRow(opts: {
  prefix: string;
  label: string;
  count: number;
  x1: number;
  y1: number;
  doorWall: DoorWall;
}): PersonalOfficeSlotLayout[] {
  const slots: PersonalOfficeSlotLayout[] = [];
  for (let i = 0; i < opts.count; i++) {
    const x1 = opts.x1 + i * STRIDE;
    const n = i + 1;
    slots.push(
      makePodFromBounds({
        slotKey: `office_${opts.prefix}_${n}`,
        nameDefault: `${opts.label} ${n}`,
        x1,
        y1: opts.y1,
        x2: x1 + POD,
        y2: opts.y1 + POD,
        doorWall: opts.doorWall,
      }),
    );
  }
  return slots;
}

/**
 * Explicit-key pod, so the HQ perimeter ring can number offices 1..N
 * sequentially (office_hq_1 …) regardless of which wall they sit on.
 */
function buildPod(opts: {
  slotKey: string;
  name: string;
  x1: number;
  y1: number;
  doorWall: DoorWall;
}): PersonalOfficeSlotLayout {
  return makePodFromBounds({
    slotKey: opts.slotKey,
    nameDefault: opts.name,
    x1: opts.x1,
    y1: opts.y1,
    x2: opts.x1 + POD,
    y2: opts.y1 + POD,
    doorWall: opts.doorWall,
  });
}

function lobbySpawns(gx: number, gy: number): Array<{ gx: number; gy: number }> {
  return [
    { gx, gy },
    { gx: gx + 1, gy },
    { gx: gx - 1, gy },
    { gx, gy: gy + 1 },
    { gx: gx + 1, gy: gy + 1 },
    { gx: gx - 1, gy: gy + 1 },
  ];
}

function toCampus(id: string, gx: number, gy: number, label: string): MapTransition {
  return {
    id,
    gx,
    gy,
    toMapId: "campus",
    // Outside that building's campus pad — filled per building below
    toSpawn: { gx, gy },
    label,
  };
}

/**
 * Outdoor plaza buildings — solid wall masses with a walk-on door pad on the
 * face toward the central plaza (not a lone square transition on empty grass).
 *
 * door: walkable transition tile (just outside / in the facade gap)
 * exteriorSpawn: where you reappear when leaving the indoor map
 */
type CampusBuilding = {
  id: string;
  mapId: string;
  name: string;
  /** Inclusive building footprint. */
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  /** Wall color hex for the solid shell. */
  color: string;
  doorWall: DoorWall;
  lobbySpawn: { gx: number; gy: number };
};

/**
 * Campus buildings — a single Nymbl HQ (MVP; more added as the team grows).
 *
 * `id: "nymbl_hq"` → the procedural pixel-art facade PNG `bldg_nymbl_hq.png`
 * (baked by `bun scripts/gen-nymbl-hq.ts`, registered in BUILDING_STYLES): brand
 * wings + glass grid + rooftop NYMBL nameplate. If that PNG ever fails to load the
 * engine falls back to drawNymblHQ (world-engine.js). `mapId: "reception"` reuses
 * the reception interior as the HQ lobby so the building stays enterable. Door S.
 */
const CAMPUS_BUILDINGS: readonly CampusBuilding[] = [
  {
    id: "nymbl_hq",
    mapId: "reception",
    name: "Nymbl",
    // Grid-x kept near the spawn's (x−y) so the HQ frames up-CENTER above the
    // player in iso (high grid-x pushes a north building off to the right edge).
    x1: 6,
    y1: 5,
    x2: 13,
    y2: 9,
    color: "#111C46",
    doorWall: "S",
    lobbySpawn: { gx: 27, gy: 18 },
  },
] as const;

function buildingDoorTile(b: CampusBuilding): { gx: number; gy: number } {
  const w = b.x2 - b.x1 + 1;
  const d = b.y2 - b.y1 + 1;
  const midX = b.x1 + Math.floor(w / 2);
  const midY = b.y1 + Math.floor(d / 2);
  if (b.doorWall === "S") return { gx: midX, gy: b.y2 };
  if (b.doorWall === "N") return { gx: midX, gy: b.y1 };
  if (b.doorWall === "E") return { gx: b.x2, gy: midY };
  return { gx: b.x1, gy: midY };
}

function buildingExteriorSpawn(b: CampusBuilding): { gx: number; gy: number } {
  const door = buildingDoorTile(b);
  if (b.doorWall === "S") return { gx: door.gx, gy: door.gy + 1 };
  if (b.doorWall === "N") return { gx: door.gx, gy: door.gy - 1 };
  if (b.doorWall === "E") return { gx: door.gx + 1, gy: door.gy };
  return { gx: door.gx - 1, gy: door.gy };
}

function buildOutdoorBuildingShell(b: CampusBuilding): MapFurniture[] {
  const items: MapFurniture[] = [];
  const door = buildingDoorTile(b);
  const w = b.x2 - b.x1 + 1;
  const d = b.y2 - b.y1 + 1;
  const prefix = `bldg-${b.id}`;

  const mass = furn(`${prefix}-mass`, "building", b.x1, b.y1, {
    w,
    d,
    block: true,
  });
  mass.c = b.color;
  mass.displayName = b.name;
  mass.doorWall = b.doorWall;
  mass.style = b.id;
  items.push(mass);

  const doorPiece = furn(`${prefix}-door`, "door", door.gx, door.gy, {});
  doorPiece.displayName = b.name;
  doorPiece.style = b.id;
  items.push(doorPiece);

  return items;
}

export function campusDoorForMap(mapId: string): {
  door: { gx: number; gy: number };
  exterior: { gx: number; gy: number };
} | null {
  const b = CAMPUS_BUILDINGS.find((x) => x.mapId === mapId);
  if (!b) return null;
  return { door: buildingDoorTile(b), exterior: buildingExteriorSpawn(b) };
}

/** Outdoor hub — a single Nymbl HQ on a cobble plaza with a central fountain. */
export function buildCampusLayout(): CampusMapLayoutDocument {
  const furniture: MapFurniture[] = [];

  // One cobble plaza south of the HQ (HQ footprint is x6–13, y5–9).
  const commonZones: CommonZoneLayout[] = [
    {
      id: "plaza",
      name: "The Plaza",
      x1: 7,
      y1: 10,
      x2: 28,
      y2: 24,
      rug: "#B8BCC6", // cobble paving
    },
  ];

  const transitions: MapTransition[] = [];
  for (const b of CAMPUS_BUILDINGS) {
    const door = buildingDoorTile(b);
    furniture.push(...buildOutdoorBuildingShell(b));
    transitions.push({
      id: `campus-to-${b.id}`,
      gx: door.gx,
      gy: door.gy,
      toMapId: b.mapId,
      toSpawn: b.lobbySpawn,
      label: b.name,
    });
  }

  // Central fountain in front of the HQ entrance + a ring of flowers.
  furniture.push(furn("plaza-fountain", "fountain", 17, 15, { block: true }));
  for (const [i, [gx, gy]] of (
    [
      [16, 14],
      [18, 14],
      [16, 16],
      [18, 16],
    ] as const
  ).entries()) {
    furniture.push(furn(`plaza-flower-${i}`, "flower", gx, gy, { block: true }));
  }

  // Benches ringing the plaza, facing the center.
  furniture.push(furn("bench-0", "bench", 9, 13, { dir: "E", seat: true }));
  furniture.push(furn("bench-1", "bench", 26, 13, { dir: "W", seat: true }));
  furniture.push(furn("bench-2", "bench", 9, 21, { dir: "E", seat: true }));
  furniture.push(furn("bench-3", "bench", 26, 21, { dir: "W", seat: true }));
  furniture.push(furn("bench-4", "bench", 14, 23, { dir: "N", seat: true }));
  furniture.push(furn("bench-5", "bench", 21, 23, { dir: "N", seat: true }));

  // Street lamps.
  for (const [i, [gx, gy]] of (
    [
      [12, 15],
      [23, 15],
      [17, 22],
    ] as const
  ).entries()) {
    furniture.push(furn(`lamp-${i}`, "streetlamp", gx, gy, { block: true }));
  }

  // Welcome sign at the plaza edge.
  furniture.push(furn("plaza-welcome", "plaza_sign", 8, 22, { block: true }));

  // Shrubs flanking the HQ + plaza corners.
  for (const [i, [gx, gy]] of (
    [
      [8, 10],
      [15, 10],
      [7, 11],
      [28, 11],
      [7, 23],
      [28, 23],
    ] as const
  ).entries()) {
    furniture.push(furn(`bush-${i}`, "bush", gx, gy, { block: true }));
  }

  // Tree / pine frame around the HQ and plaza (not a forest wall).
  const canopy: Array<[string, number, number]> = [
    ["tree", 8, 3],
    ["pine", 12, 3],
    ["tree", 5, 5],
    ["pine", 15, 5],
    ["tree", 4, 15],
    ["pine", 30, 11],
    ["tree", 30, 18],
    ["pine", 6, 23],
    ["tree", 29, 23],
    ["pine", 14, 26],
    ["tree", 22, 26],
    ["pine", 3, 20],
  ];
  canopy.forEach(([kind, gx, gy], i) => {
    furniture.push(furn(`canopy-${i}`, kind, gx, gy, { block: true }));
  });

  return {
    id: "campus",
    cols: MAP_COLS,
    rows: MAP_ROWS,
    offices: [],
    commonZones,
    furniture,
    // Spawn on open plaza cobble, south of the fountain, looking up at the HQ.
    spawnPoints: lobbySpawns(22, 20),
    transitions,
  };
}

/**
 * The Nymbl HQ interior — the flagship office you enter from the plaza.
 *
 * A complete, well-designed floor rather than an open-plan sketch:
 *
 *   - A PERIMETER RING of 22 individual private offices (claimable pods): 8 down
 *     the west wall (doors face E), 8 down the east wall (doors face W), and 6
 *     across the north wall (doors face S). Each pod auto-furnishes a desk + chair
 *     and a glass facade that frosts when its door is closed.
 *   - A CENTRAL MEETING CORE: two glass CONFERENCE rooms (Conference A/B, all-hands
 *     tables) flank a central "town-square" COLLABORATION HUB, with three HUDDLE
 *     rooms below for small syncs. Private rooms auto-furnish by `roomKind`.
 *   - The SOUTH band is the arrival experience: RECEPTION on the entrance sightline
 *     (mascot + desk), a CAFÉ / kitchenette to the west, and a LOUNGE to the east.
 *     A shared TEAM FLOOR of open hot-desks sits just north of it.
 *   - Circulation is a loop: a north egress corridor (y6–8), two side egress
 *     corridors (west x6–7, east x46–47) and the central hub tie every office door
 *     and room door back to the entrance — no dead ends.
 *
 * Offices carry `office_hq_1..22` keys so they never collide with the department
 * pods, and each office/room satisfies map-layout-validate (offices ≥5×5 inside a
 * 1-tile border, no office/zone overlap, meeting ≥6×6, huddle ≥4×4).
 */
export function buildReceptionLayout(): CampusMapLayoutDocument {
  const pad = campusDoorForMap("reception");
  const exit = toCampus("reception-to-campus", 27, 39, "Campus");
  if (pad) exit.toSpawn = pad.exterior;

  const furniture: MapFurniture[] = [];
  const add = (id: string, kind: string, gx: number, gy: number, o?: Parameters<typeof furn>[4]) =>
    furniture.push(furn(id, kind, gx, gy, o));

  // ── Perimeter office ring (22 claimable private offices) ──────────────────
  // Sequential office_hq_N keys regardless of which wall a pod sits on.
  const offices: PersonalOfficeSlotLayout[] = [];
  const pushPod = (x1: number, y1: number, doorWall: DoorWall): void => {
    const num = offices.length + 1;
    offices.push(
      buildPod({ slotKey: `office_hq_${num}`, name: `Office ${num}`, x1, y1, doorWall }),
    );
  };
  // West wall — doors face E into the west egress corridor (x6–7).
  for (const y1 of [1, 5, 9, 13, 17, 21, 25, 29]) pushPod(1, y1, "E");
  // East wall — doors face W into the east egress corridor (x46–47).
  for (const y1 of [1, 5, 9, 13, 17, 21, 25, 29]) pushPod(48, y1, "W");
  // North wall — doors face S into the north egress corridor (y6–8).
  for (const x1 of [16, 20, 24, 28, 32, 36]) pushPod(x1, 1, "S");

  // ── Team floor — open hot-desk clusters (avoid huddle door columns x11/26/41) ─
  const hotDesk = (id: string, x: number): void => {
    add(`hq-hot-${id}-desk`, "desk", x, 29, { dir: "S", block: true });
    add(`hq-hot-${id}-chair`, "chair", x, 30, { dir: "N", seat: true });
  };
  for (const [i, x] of [8, 10, 13].entries()) hotDesk(`w${i}`, x); // west cluster
  for (const [i, x] of [38, 40, 43].entries()) hotDesk(`e${i}`, x); // east cluster
  add("hq-team-printer", "printer", 16, 28, { block: true });
  add("hq-team-plant-w", "plant", 9, 32, { block: true });
  add("hq-team-plant-e", "plant", 44, 32, { block: true });

  // ── Collaboration hub (central town square) — informal seating + greenery ──
  add("hq-hub-plant-n", "plant", 26, 8, { block: true });
  add("hq-hub-sofa-a", "sofa1", 22, 15, { dir: "E", seat: true });
  add("hq-hub-sofa-b", "sofa1", 22, 16, { dir: "E", seat: true });
  add("hq-hub-coffee", "coffeetable", 24, 15, { block: true });
  add("hq-hub-shelf-a", "bookshelf", 30, 9, { block: true });
  add("hq-hub-shelf-b", "bookshelf", 30, 11, { block: true });
  add("hq-hub-plant-s", "plant", 26, 19, { block: true });

  // ── Reception (south-center, entrance sightline; keep x26–28/y37–39 walkable) ─
  add("hq-rec-desk", "reception", 30, 35, { block: true });
  add("hq-rec-fox", "fox", 23, 36, { block: true }); // mascot landmark
  add("hq-rec-sign", "sign", 23, 34, { block: true });
  add("hq-rec-plant-a", "plant", 22, 40, { block: true });
  add("hq-rec-plant-b", "plant", 32, 34, { block: true });

  // ── Café & kitchen (SW arrival corner) ────────────────────────────────────
  add("hq-cafe-counter", "counter", 2, 35, { w: 3, d: 1, block: true });
  add("hq-cafe-coffee", "coffee", 6, 35, { block: true });
  add("hq-cafe-cooler", "cooler", 8, 35, { block: true });
  add("hq-cafe-fridge", "fridge", 2, 38, { block: true });
  add("hq-cafe-table", "table", 11, 37, { w: 2, d: 1, block: true });
  add("hq-cafe-stool-a", "stool", 11, 38, { dir: "N", seat: true });
  add("hq-cafe-stool-b", "stool", 12, 38, { dir: "N", seat: true });
  add("hq-cafe-sofa", "sofa1", 16, 35, { dir: "W", seat: true });
  add("hq-cafe-coffeetable", "coffeetable", 18, 36, { block: true });
  add("hq-cafe-plant", "plant", 19, 40, { block: true });

  // ── Lounge (SE arrival corner, social energy) ─────────────────────────────
  add("hq-lounge-sofa-a", "sofa1", 35, 35, { dir: "E", seat: true });
  add("hq-lounge-sofa-b", "sofa1", 35, 36, { dir: "E", seat: true });
  add("hq-lounge-coffee", "coffeetable", 37, 35, { block: true });
  add("hq-lounge-couch-a", "couch", 39, 35, { dir: "W", seat: true });
  add("hq-lounge-couch-b", "couch", 39, 36, { dir: "W", seat: true });
  add("hq-lounge-beanbag", "beanbag", 42, 38, { seat: true });
  add("hq-lounge-arcade", "arcade", 46, 35, { block: true });
  add("hq-lounge-jukebox", "jukebox", 48, 35, { block: true });
  add("hq-lounge-plant-a", "plant", 34, 40, { block: true });
  add("hq-lounge-plant-b", "plant", 51, 40, { block: true });

  // ── Corridor greenery (soft dividers; never on office/room door tiles) ─────
  add("hq-corr-plant-w1", "plant", 7, 12, { block: true });
  add("hq-corr-plant-w2", "plant", 7, 24, { block: true });
  add("hq-corr-plant-e1", "plant", 46, 12, { block: true });
  add("hq-corr-plant-e2", "plant", 46, 24, { block: true });
  add("hq-corr-plant-n1", "plant", 14, 7, { block: true });
  add("hq-corr-plant-n2", "plant", 43, 7, { block: true });

  return {
    id: "reception",
    cols: MAP_COLS,
    rows: MAP_ROWS,
    offices,
    // Zones never overlap offices or each other (validator rejects both). Meeting
    // rooms ≥6×6, huddles ≥4×4. hq_-prefixed ids avoid editor template collisions.
    commonZones: [
      // Central meeting core
      {
        id: "hq_conf_a",
        name: "Conference A",
        x1: 9,
        y1: 9,
        x2: 21,
        y2: 18,
        rug: "#DCECF8",
        priv: true,
        roomKind: "meeting",
      },
      {
        id: "hq_conf_b",
        name: "Conference B",
        x1: 32,
        y1: 9,
        x2: 44,
        y2: 18,
        rug: "#D8E8F4",
        priv: true,
        roomKind: "meeting",
      },
      {
        id: "hq_huddle_w",
        name: "Huddle West",
        x1: 9,
        y1: 21,
        x2: 14,
        y2: 27,
        rug: "#E8E0F4",
        priv: true,
        roomKind: "huddle",
      },
      {
        id: "hq_huddle_c",
        name: "Huddle Central",
        x1: 23,
        y1: 21,
        x2: 30,
        y2: 27,
        rug: "#E4E0F2",
        priv: true,
        roomKind: "huddle",
      },
      {
        id: "hq_huddle_e",
        name: "Huddle East",
        x1: 39,
        y1: 21,
        x2: 44,
        y2: 27,
        rug: "#E8E0F4",
        priv: true,
        roomKind: "huddle",
      },
      // Open collaboration
      {
        id: "hq_collab_hub",
        name: "Collaboration Hub",
        x1: 22,
        y1: 8,
        x2: 31,
        y2: 20,
        rug: "#E4EEFB",
      },
      { id: "hq_team_floor", name: "Team Floor", x1: 8, y1: 28, x2: 45, y2: 33, rug: "#E6F5EC" },
      // Arrival band
      { id: "hq_cafe", name: "Café & Kitchen", x1: 1, y1: 34, x2: 20, y2: 40, rug: "#F5EEE2" },
      { id: "hq_reception", name: "Reception", x1: 22, y1: 34, x2: 32, y2: 40, rug: "#D4E4F2" },
      { id: "hq_lounge", name: "The Lounge", x1: 34, y1: 34, x2: 52, y2: 40, rug: "#E8DCF0" },
    ],
    furniture,
    // Spawn just inside the entrance, in the reception zone, facing north into HQ.
    spawnPoints: lobbySpawns(27, 37),
    transitions: [exit],
  };
}

export function buildCafeLayout(): CampusMapLayoutDocument {
  const pad = campusDoorForMap("cafe");
  const exit = toCampus("cafe-to-campus", 20, 28, "Campus");
  if (pad) exit.toSpawn = pad.exterior;
  return {
    id: "cafe",
    cols: MAP_COLS,
    rows: MAP_ROWS,
    offices: [],
    commonZones: [
      {
        id: "cafe_floor",
        name: "Cafe",
        x1: 14,
        y1: 12,
        x2: 34,
        y2: 26,
        rug: "#E8DCF0",
      },
    ],
    furniture: [
      furn("cafe-table-a", "coffeetable", 22, 18, { block: true }),
      furn("cafe-table-b", "coffeetable", 28, 18, { block: true }),
      furn("cafe-couch-a", "couch", 18, 17, { dir: "E", seat: true }),
      furn("cafe-couch-b", "couch", 18, 18, { dir: "E", seat: true }),
      furn("cafe-sofa", "sofa1", 30, 17, { dir: "W", seat: true }),
      furn("cafe-stool-a", "stool", 22, 20, { dir: "N", seat: true }),
      furn("cafe-stool-b", "stool", 28, 20, { dir: "N", seat: true }),
      furn("cafe-cooler", "cooler", 16, 14, { block: true }),
      furn("cafe-jukebox", "jukebox", 32, 14, { block: true }),
      furn("cafe-plant", "plant", 16, 24, { block: true }),
      furn("cafe-beanbag", "beanbag", 26, 22, { seat: true }),
    ],
    spawnPoints: lobbySpawns(20, 18),
    transitions: [exit],
  };
}

/**
 * Department floor: corridor + pod row(s) with doors facing the corridor.
 * Exit to campus at south lobby.
 */
function buildDeptFloor(opts: {
  mapId: string;
  prefix: string;
  label: string;
  podCount: number;
  /** Two rows when true (Delivery). */
  doubleRow?: boolean;
}): CampusMapLayoutDocument {
  const yNorth = 10;
  const ySouth = 20;
  let offices: PersonalOfficeSlotLayout[] = [];
  if (opts.doubleRow) {
    // 10 north (doors face S) + 10 south (doors face N)
    const half = Math.floor(opts.podCount / 2);
    offices = [
      ...buildPodRow({
        prefix: `${opts.prefix}_n`,
        label: opts.label,
        count: half,
        x1: 8,
        y1: yNorth,
        doorWall: "S",
      }),
      ...buildPodRow({
        prefix: `${opts.prefix}_s`,
        label: opts.label,
        count: opts.podCount - half,
        x1: 8,
        y1: ySouth,
        doorWall: "N",
      }),
    ];
    // Renumber slot keys to sequential prefix_1..N for stable claim order
    offices = offices.map((o, i) =>
      makePodFromBounds({
        slotKey: `office_${opts.prefix}_${i + 1}`,
        nameDefault: `${opts.label} ${i + 1}`,
        x1: o.x1,
        y1: o.y1,
        x2: o.x2,
        y2: o.y2,
        doorWall: o.doorWall,
      }),
    );
  } else {
    offices = buildPodRow({
      prefix: opts.prefix,
      label: opts.label,
      count: opts.podCount,
      x1: 8,
      y1: yNorth,
      doorWall: "S",
    });
  }

  const pad = campusDoorForMap(opts.mapId);
  const exit = toCampus(`${opts.mapId}-to-campus`, 27, 32, "Campus");
  if (pad) exit.toSpawn = pad.exterior;

  const commonZones: CommonZoneLayout[] = [
    {
      id: "corridor",
      name: "Corridor",
      x1: 8,
      y1: opts.doubleRow ? 15 : 15,
      x2: 48,
      y2: opts.doubleRow ? 19 : 18,
      rug: "#E8F2FB",
    },
  ];

  const furniture: MapFurniture[] = [
    furn(`${opts.mapId}-cooler`, "cooler", 10, 16, { block: true }),
    furn(`${opts.mapId}-plant`, "plant", 46, 16, { block: true }),
    furn(`${opts.mapId}-printer`, "printer", 12, 17, { block: true }),
  ];

  // Lobby south of both pod rows (south pods sit at y=20..24 when double-row).
  const spawnGy = opts.doubleRow ? 28 : 22;
  return {
    id: opts.mapId,
    cols: MAP_COLS,
    rows: MAP_ROWS,
    offices: cloneOffices(offices),
    commonZones,
    furniture,
    spawnPoints: lobbySpawns(27, spawnGy),
    transitions: [exit],
  };
}

export function buildDeliveryLayout(): CampusMapLayoutDocument {
  return buildDeptFloor({
    mapId: "delivery",
    prefix: "delivery",
    label: "Delivery",
    podCount: 20,
    doubleRow: true,
  });
}

export function buildPostDeliveryLayout(): CampusMapLayoutDocument {
  return buildDeptFloor({
    mapId: "post_delivery",
    prefix: "post_delivery",
    label: "Post Delivery",
    podCount: 7,
  });
}

export function buildPeopleMarketingLayout(): CampusMapLayoutDocument {
  return buildDeptFloor({
    mapId: "people_marketing",
    prefix: "people_marketing",
    label: "People + Marketing",
    podCount: 5,
  });
}

export function buildCustomerSuccessLayout(): CampusMapLayoutDocument {
  return buildDeptFloor({
    mapId: "customer_success",
    prefix: "customer_success",
    label: "Customer Success",
    podCount: 10,
  });
}

/** All code-default layouts keyed by map id. */
export function buildAllDefaultLayouts(): Record<string, CampusMapLayoutDocument> {
  return {
    campus: buildCampusLayout(),
    reception: buildReceptionLayout(),
    cafe: buildCafeLayout(),
    delivery: buildDeliveryLayout(),
    post_delivery: buildPostDeliveryLayout(),
    people_marketing: buildPeopleMarketingLayout(),
    customer_success: buildCustomerSuccessLayout(),
  };
}

/** Union of personal office slots across all default dept buildings (42). */
export function allDefaultOfficeSlots(): PersonalOfficeSlotLayout[] {
  const layouts = buildAllDefaultLayouts();
  const out: PersonalOfficeSlotLayout[] = [];
  for (const layout of Object.values(layouts)) {
    for (const o of layout.offices) out.push(o);
  }
  return out;
}
